import React, { useCallback, useState } from 'react';

import ConfirmModal from 'components/Modal/ConfirmModal/ConfirmModal';
import { ConfirmDialog } from '../modals/confirmDialogs';

type Session = {
  dialog: ConfirmDialog;
  resolve: (confirmed: boolean) => void;
};

/**
 * Wraps the v1 {@link ConfirmModal} in an await-based API. `showConfirmModal(dialog)` returns a
 * promise that resolves `true` when the user confirms the primary action and `false` when they
 * cancel/dismiss. This lets {@link useJourneyActions} drive the transactional dialogs
 * (schedule-future / expired / deactivate / save-guards) with linear `async`/`await` control flow
 * instead of v1's callback-stuffed `primaryButton.onClick` closures.
 *
 * Informational dialogs (no `cancelLabel`) resolve `true` on the single acknowledging button.
 */
export default function useTransactionalConfirmModal() {
  const [activeConfirm, setActiveConfirm] = useState<Session | null>(null);

  const close = useCallback((confirmed: boolean) => {
    setActiveConfirm((current) => {
      current?.resolve(confirmed);
      return null;
    });
  }, []);

  const showConfirmModal = useCallback(
    (dialog: ConfirmDialog) =>
      new Promise<boolean>((resolve) => {
        setActiveConfirm((current) => {
          current?.resolve(false);
          return { dialog, resolve };
        });
      }),
    [],
  );

  const ConfirmModalSlot = useCallback((): JSX.Element | null => {
    if (!activeConfirm) return null;

    const { dialog } = activeConfirm;

    return (
      <ConfirmModal
        isVisible
        title={dialog.title}
        content={dialog.content as string | JSX.Element}
        contentIcon={dialog.contentIcon}
        isLaunching={dialog.isLaunching}
        isAudienceEmpty={dialog.isAudienceEmpty}
        primaryButton={{
          name: dialog.confirmLabel,
          disabled: false,
          onClick: () => close(true),
        }}
        cancelButton={dialog.cancelLabel ? { name: dialog.cancelLabel, onClick: () => close(false) } : undefined}
      />
    );
  }, [activeConfirm, close]);

  return { ConfirmModalSlot, showConfirmModal };
}
